import React, { forwardRef, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parseLocalDate, formatLocalYmd } from '../utils/dates';
import './DateTimePicker.css';

// Customer-facing date (RV) or date+time (cargo) picker. Thin wrapper
// over react-datepicker that handles the YMD-string / Date conversion at
// the boundary using the local-fields helpers, so the picker never goes
// through .toISOString() — eliminating the TZ-shift class of bugs that
// plagued the hand-rolled Calendar.js.
//
// Props:
//   - value:        YMD string (e.g. '2026-08-15'). '' for unset.
//   - onChange:     called with the new YMD string ('' when cleared).
//   - mode:         'date' (RV — date only) or 'datetime' (cargo — date+time).
//   - time:         HH:mm string, only meaningful when mode='datetime'.
//   - onTimeChange: called with the new HH:mm string on time changes.
//   - minDate:      Date | YMD string. Cells before this are disabled.
//   - bookedDateStrings: optional Set of YMD strings to gray out as booked.
//   - placeholder:  text shown in the button when value is empty.
//   - id, className, ariaLabel: standard pass-throughs for the trigger button.
//
// UX constraints (per product spec):
//   - No typing. The trigger is a <button>, not an <input>.
//   - 15-minute time slots only when mode='datetime'.

const TriggerButton = forwardRef(function TriggerButton(
  { value, onClick, placeholder, className, id, ariaLabel },
  ref
) {
  return (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      id={id}
      aria-label={ariaLabel}
      className={`datetime-picker-trigger${value ? ' has-value' : ''}${className ? ` ${className}` : ''}`}
    >
      {value || placeholder || 'Select date'}
    </button>
  );
});

function toDateOrNull(input) {
  if (!input) return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  return parseLocalDate(input);
}

export default function DateTimePicker({
  value,
  onChange,
  mode = 'date',
  time = '',
  onTimeChange,
  minDate,
  maxDate,
  bookedDateStrings,
  placeholder,
  id,
  className,
  ariaLabel,
}) {
  const isDateTime = mode === 'datetime';

  // The Date object the picker operates on carries both the date and
  // (for datetime mode) the wall-clock time. We rebuild it from props
  // so external state changes flow in cleanly.
  const selected = useMemo(() => {
    const d = toDateOrNull(value);
    if (!d) return null;
    if (isDateTime && time) {
      const [h, m] = String(time).split(':').map(Number);
      if (!Number.isNaN(h) && !Number.isNaN(m)) {
        d.setHours(h, m, 0, 0);
      }
    }
    return d;
  }, [value, time, isDateTime]);

  const minDateValue = useMemo(() => toDateOrNull(minDate), [minDate]);
  // Upper bound (e.g. the check-out picker is capped at the next booking's
  // check-in day minus the trailer's turnaround buffer).
  const maxDateValue = useMemo(() => toDateOrNull(maxDate), [maxDate]);

  const handleChange = (date) => {
    if (!date) {
      onChange?.('');
      if (isDateTime) onTimeChange?.('');
      return;
    }
    const ymd = formatLocalYmd(date);
    if (ymd !== value) onChange?.(ymd);
    if (isDateTime) {
      const hh = String(date.getHours()).padStart(2, '0');
      const mm = String(date.getMinutes()).padStart(2, '0');
      const hhmm = `${hh}:${mm}`;
      if (hhmm !== time) onTimeChange?.(hhmm);
    }
  };

  // Gray out booked dates. Compare via YMD strings so we don't accidentally
  // round-trip through any Date math the picker does internally.
  const filterDate = (date) => {
    if (!bookedDateStrings) return true;
    const ymd = formatLocalYmd(date);
    if (bookedDateStrings instanceof Set) return !bookedDateStrings.has(ymd);
    return !Array.from(bookedDateStrings).includes(ymd);
  };

  const dateFormat = isDateTime ? 'MMM d, yyyy h:mm aa' : 'MMM d, yyyy';

  return (
    <DatePicker
      selected={selected}
      onChange={handleChange}
      minDate={minDateValue || undefined}
      maxDate={maxDateValue || undefined}
      filterDate={filterDate}
      showTimeSelect={isDateTime}
      timeIntervals={15}
      timeCaption="Time"
      timeFormat="h:mm aa"
      dateFormat={dateFormat}
      placeholderText={placeholder}
      customInput={
        <TriggerButton
          placeholder={placeholder}
          className={className}
          id={id}
          ariaLabel={ariaLabel}
        />
      }
      // Prevent keyboard from typing into the (button) trigger.
      onChangeRaw={(e) => e?.preventDefault?.()}
      popperPlacement="bottom-start"
      shouldCloseOnSelect={!isDateTime}
    />
  );
}
